import React, { useEffect, useState } from "react";
import { Modal, Typography, Stack, Slide, Tab, Tabs, Box, IconButton } from "@mui/material";

import ReactPerfectScrollbar from "react-perfect-scrollbar";

import { ReactComponent as ManagedomainIcon } from "../../../../vendor/_new/icon/sites/manage_domain.svg";
import { ReactComponent as CloseIcon } from "../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as CopyIcon } from "../../../../vendor/_new/icon/sites/copy.svg";

import colors from "../../../../utils/colors";
import copy from "copy-to-clipboard";

import ManageDomainConfig from "./ManageDomainConfig";
import ManageDomainSubdomain from "./ManageDomainSubdomain";
import useSite from "../../../../hooks/user/useSite";
import usePermissions from "../../../../hooks/usePermissions";

const ManageDomainModal = ({ openManageDomainModal, handleManageDomainClose, selectedSite, sitePermissions }) => {
  const { viewSite } = useSite();
  const { hasPermission, PERMISSIONS } = usePermissions();
  const [manageDomainTab, setManageDomainTab] = useState("config");
  const [site, setSite] = useState(selectedSite);
  const [copied, setCopied] = useState(false);
  
  // Use site-specific permissions if provided, otherwise fall back to global check
  const canEdit = sitePermissions ? sitePermissions.canWrite() : hasPermission(PERMISSIONS.SITES_WRITE);

  useEffect(() => {
    if (openManageDomainModal && selectedSite) {
      viewSite(selectedSite?.id).then((_site) => {
        setSite(_site);
      });
    }
  }, [openManageDomainModal, viewSite, selectedSite]);

  const handleCopyDomain = () => {
    if (selectedSite?.site_id || selectedSite?.domain) {
      copy(selectedSite?.site_id || selectedSite?.domain);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <React.Fragment>
      <Modal open={openManageDomainModal} onClose={handleManageDomainClose}>
        <Slide direction="left" in={openManageDomainModal} mountOnEnter unmountOnExit>
          <Box
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "33%",
              minWidth: 520,
              height: "100vh",
              backgroundColor: "#fff",
              boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
              zIndex: 1300,
              borderTopLeftRadius: "8px",
              outline: "none",
              border: "none",
            }}
          >
            <ReactPerfectScrollbar>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ p: 4, borderBottom: `1px solid ${colors.neutral[200]}` }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "12px",
                      backgroundColor: "#EEF0FE",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ManagedomainIcon style={{ width: 28, height: 28, color: "#6371F7" }} />
                  </Box>
                  <Box>
                    <Typography sx={ { fontSize: 20, fontWeight: "medium", color: colors.neutral[1000] }}>
                      {canEdit ? "Manage Domain" : "View Domain"}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography sx={{ fontFamily: "monospace", fontSize: 13, color: "#6371F7" }}>
                        {selectedSite?.site_id || selectedSite?.domain || "—"}
                      </Typography>
                      <IconButton size="small" onClick={handleCopyDomain} sx={{ p: 0.5 }}>
                        <CopyIcon style={{ width: 14, height: 14, color: copied ? "#10B981" : "#6371F7" }} />
                      </IconButton>
                    </Stack>
                  </Box>
                </Stack>
                <IconButton onClick={handleManageDomainClose}>
                  <CloseIcon style={{ width: 24, height: 24, color: colors.neutral[600] }} />
                </IconButton>
              </Stack>

              <Box sx={{ px: 4, pt: 3, pb: 2 }}>
                <Tabs
                  value={manageDomainTab}
                  onChange={(_, v) => setManageDomainTab(v)}
                  sx={{
                    minHeight: 40,
                    "& .MuiTabs-flexContainer": { 
                      gap: "4px",
                    },
                    "& .MuiTabs-indicator": { 
                      height: 2, 
                      backgroundColor: "#6371F7",
                      borderRadius: "2px",
                    },
                    "& button": { 
                      minWidth: "auto",
                      minHeight: 40,
                      px: 2,
                      py: 1,
                      borderRadius: "8px 8px 0 0",
                    },
                    "& button .MuiTypography-root": { 
                      color: colors.neutral[600],
                      fontWeight: "regular",
                    },
                    "& .Mui-selected": {
                      backgroundColor: "rgba(99, 113, 247, 0.08)",
                    },
                    "& .Mui-selected .MuiTypography-root": { 
                      color: "#6371F7",
                      fontWeight: "medium",
                    },
                  }}
                >
                  <Tab
                    value="config"
                    label={<Typography sx={ { fontSize: 14 }}>Configuration</Typography>}
                  />
                  <Tab
                    value="subdomain"
                    label={<Typography sx={ { fontSize: 14 }}>Subdomains</Typography>}
                  />
                </Tabs>
              </Box>

              <Box sx={{ p: 4, pt: 2, height: `calc(100vh - 180px)` }}>
                {manageDomainTab === "config" && <ManageDomainConfig site={site} handleManageDomainClose={handleManageDomainClose} canEdit={canEdit} />}
                {manageDomainTab === "subdomain" && (
                  <ManageDomainSubdomain site={site} handleManageDomainClose={handleManageDomainClose} canEdit={canEdit} />
                )}
              </Box>
            </ReactPerfectScrollbar>
          </Box>
        </Slide>
      </Modal>
    </React.Fragment>
  );
};
export default ManageDomainModal;
